import {
  Component,
  EventEmitter,
  Input,
  AfterViewInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges, OnInit
} from '@angular/core';
import intlTelInput from 'intl-tel-input';
import utils from 'intl-tel-input/build/js/utils.js';

enum ERROR_MESSAGES {
  REQUIRED = 'Phone Number is required',
}

@Component({
  selector: 'beaconstac-intl-tel-input',
  templateUrl: './intl-tel-input.component.html',
  styleUrls: ['./intl-tel-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class IntlTelInputComponent implements AfterViewInit, OnChanges, OnInit {

  @ViewChild('telInput') telInput: HTMLInputElement;

  @Input() width: string;
  @Input() disabled: boolean;
  @Input() label: string;
  @Input() showLabel: boolean;
  @Input() isRequired: boolean;
  @Input() countryNameCode: string;
  @Input() value: string;

  @Output() phoneNumberChange = new EventEmitter<string>();
  @Output() countryChange = new EventEmitter<any>();
  @Output() onBlur = new EventEmitter<any>();

  phoneNumber: string;
  selectedCountryCallingCode: any;
  iti: any;
  showRequiredError: boolean;
  errorMessage: string;
  isMobileScreen: boolean;

  // Elements
  input: HTMLInputElement;
  flagElement: HTMLElement;
  countryCodeElement: HTMLElement;
  parentElementOfInput: HTMLElement;
  mobileDropdown: HTMLElement;
  dropdown: HTMLElement;


  ngOnChanges(changes: SimpleChanges) {
    if (changes.disabled) {
      this.updateDisabledState();
    }

    if (changes.isRequired) {
      this.updateRequiredState();
    }

    if (changes.value) {
      this.phoneNumber = changes.value.currentValue;
      this.setInputElementPadding();
    }

    if (changes.countryNameCode && !changes.countryNameCode.firstChange) {
      this.updateCountry();
    }
  }

  ngOnInit() {
    this.phoneNumber = this.value;
    this.checkMobileScreen();
  }

  ngAfterViewInit() {
    this.input = document.getElementById('phone') as HTMLInputElement;

    this.initializeInput();

    this.initializeVariables();
    this.setCountryCodePrefix();
    this.setInputElementPadding();
    this.setUpEventListenersForKeyUp();
    this.setUpEventListenersForKeyDown();

    this.setUpEventListenersForCountryChange();
    this.setUpEventListenersForFlagContainer();
    this.setUpResizeEventListener();
    this.trackClickOnDropdown();

    if (this.isRequired && !this.phoneNumber) {
      this.showRequiredError = true;
      this.errorMessage = ERROR_MESSAGES.REQUIRED;
    }
    this.updateCountry();
  }

  updateCountry() {
    if (this.iti && this.countryNameCode) {
      this.iti.setCountry(this.countryNameCode);
      const countryData = this.iti.getSelectedCountryData();
      this.selectedCountryCallingCode = countryData.dialCode;

      this.countryChange.emit({
        countryCallingCode: this.selectedCountryCallingCode,
        countryNameCode: countryData.iso2
      });

      this.setInputElementPadding();
    }
  }

  checkMobileScreen() {
    if (window.innerWidth <= 768) {
      this.isMobileScreen = true;
    }
  }

  initializeInput() {
    this.iti = intlTelInput(this.input, {
      initialCountry: this.countryNameCode || 'US',
      geoIpLookup: callback => {
        const countryCode = this.countryNameCode || 'US';
        callback(countryCode);
      },
      separateDialCode: true,
      nationalMode: false,
      formatOnDisplay: false,
      utilsScript: utils
    });
  }

  initializeVariables() {
    this.flagElement = document.querySelector('.iti__selected-flag') as HTMLElement;
    this.countryCodeElement = document.getElementsByClassName('iti__selected-dial-code')[0] as HTMLElement;
    this.parentElementOfInput = document.getElementsByClassName('iti--allow-dropdown')[0] as HTMLElement;
    this.selectedCountryCallingCode = this.iti.getSelectedCountryData().dialCode;
    this.input.style.width = this.width;
    this.parentElementOfInput.style.width = this.width;
  }


  setInputElementPadding() {
    if (this.countryCodeElement) {
      const rect = this.countryCodeElement.getBoundingClientRect();
      const widthWithMarginPadding = rect.width;
      // requestAnimationFrame updates padding just before the browser repaints.
      requestAnimationFrame(() => {
        this.input.style.setProperty(
          'padding-left',
          `${widthWithMarginPadding + 75}px`,
          'important'
        );
      });
    }
  }

  setCountryCodePrefix() {
    if (this.countryCodeElement && this.parentElementOfInput) {
      this.parentElementOfInput.insertBefore(this.countryCodeElement, this.parentElementOfInput.children[2]);
    }
  }

  setUpEventListenersForFlagContainer() {
    if (this.flagElement) {
      this.flagElement.addEventListener('focus', () => {
        this.flagElement.classList.add('flag-element-active');
      });

      document.addEventListener('click', (event) => {
        if (this.isMobileScreen) {
          this.setDropdownOnMobileScreen();
          this.adjustDropdownStylesOnMobile();
        }
        if (!this.flagElement.contains(event.target as Node)) {
          this.flagElement.classList.remove('flag-element-active');
        }
      });
    }
  }

  setUpEventListenersForCountryChange() {
    this.input.addEventListener('countrychange', () => {
      this.setCountryCodePrefix();
      this.setInputElementPadding();
      const countryData = this.iti.getSelectedCountryData();
      this.selectedCountryCallingCode = countryData.dialCode;
      this.countryChange.emit({countryCallingCode: this.selectedCountryCallingCode, countryNameCode: countryData.iso2});
    });
  }

  setUpEventListenersForKeyUp() {
    this.input.addEventListener('keyup', () => {
      this.setInputElementPadding();
    });
  }

  setUpEventListenersForKeyDown() {
    this.input.addEventListener('keydown', (event) => {
      this.setInputElementPadding();
      if (event.key === ' ') {
        event.preventDefault();
        const start = this.input.selectionStart || 0;
        this.input.setRangeText(' ', start, start, 'end');
      }
    });
  }

  setUpResizeEventListener() {
    window.addEventListener('resize', () => {
      if (window.innerWidth <= 768) {
        this.setDropdownOnMobileScreen();
        this.adjustDropdownStylesOnMobile();
      }
    });
  }

  trackClickOnDropdown() {
    document.body.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      if (target && target.closest('.iti__highlight')) {
        this.setInputElementPadding();
      }
    });
  }

  setDropdownOnMobileScreen() {
    const dropdown = document.querySelector('.iti.iti--container');
    const flagContainer = document.querySelector('.iti__flag-container');

    if (dropdown && flagContainer && flagContainer.children.length >= 1) {
      flagContainer.insertBefore(dropdown, flagContainer.children[1]);
    }
  }

  // Did not initialise this in OnAfterView since these will not be in the DOM.
  adjustDropdownStylesOnMobile() {
    const mobileDropdown = document.querySelector('.iti-mobile .iti--container') as HTMLElement;
    const dropdown = document.querySelector('.iti--container') as HTMLElement;

    if (window.innerWidth <= 768) {
      if (mobileDropdown) {
        mobileDropdown.classList.add('mobile-dropdown');
      }
      if (dropdown) {
        dropdown.classList.add('remove-position');
      }
    }
  }

  updateDisabledState() {
    if (!this.input) {
      return;
    }
    this.input.disabled = this.disabled;
    this.flagElement.classList.toggle('disabled-flag', this.disabled);
  }

  updateRequiredState() {
    this.showRequiredError = this.isRequired && !this.phoneNumber;
    this.errorMessage = ERROR_MESSAGES.REQUIRED;
  }

  onPhoneNumberChange() {
    if (!this.iti) {
      return;
    }
    const userInput = this.input.value;
    this.phoneNumberChange.emit(userInput);
  }

    onInputBlur() {
      this.onBlur.emit();
    }
}
